import React from 'react'

import { Card } from '@acorns/web-components'
import { Heading } from '@acorns/web-components'
import styled from 'styled-components'

import AcornsLogoSvgPath from 'assets/icons/acorns-logo.svg'
import BottomLeavesSvgPath from 'assets/icons/leaves-bottom.svg'
import TopLeavesSvgPath from 'assets/icons/leaves-top.svg'
import { theme } from 'theme'

type Props = {
  children: React.ReactNode[]
  copy: string
  subcopy?: string
  boldedSubcopy?: string
  buttonText?: string
  buttonAction?: () => void
}

// Note: We render a full viewport-sized container to
// center the Loader component in
const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100vw;
  min-height: 100vh;
  top: 0;
  left: 0;
  overflow: hidden;
  background: ${theme('colors.ivory')};
  padding: 20px;
  position: absolute;
  z-index: 0;
`

const FormContainer = styled(Card)`
  max-width: 500px;
  padding: 40px 50px;
  position: relative;

  @media screen and (max-width: ${theme('breakpoints.sm')}px) {
    padding: 30px 20px 20px 20px;
  }
`

const Leaves = styled.img`
  position: absolute;
  z-index: -1;
`

const TopLeaf = styled(Leaves)`
  width: 50%;
  top: -155px;
  left: -170px;

  @media screen and (max-width: ${theme('breakpoints.sm')}px) {
    left: 67%;
    top: -107px;
  }
`

const BottomLeaf = styled(Leaves)`
  width: 55%;
  bottom: -153px;
  right: -160px;

  @media screen and (max-width: ${theme('breakpoints.sm')}px) {
    left: -22%;
    bottom: -100px;
  }
`

const HeaderContainer = styled.div`
  text-align: center;
`

const LogoContainer = styled.div`
  min-height: 45px;
`

const HeaderCopy = styled(Heading)`
  padding: 10px 0 5px 0;
  color: ${theme('colors.slate')};
`

const HeaderSubcopy = styled.div`
  padding: 10px 0 0px 0;
  font-size: 14px;
  font-weight: normal;
  line-height: 22px;
  color: ${theme('colors.slate')};
`

const HeaderSubcopyBolded = styled.span`
  padding: 10px 0 0px 0;
  font-size: 14px;
  font-weight: bold;
  line-height: 22px;
  color: ${theme('colors.slate')};
`

const HeaderButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: ${theme('colors.green')};
  border: 0;
  background: #fff;
  padding-top: 10px;
  padding-bottom: 29px;
  &:hover {
    text-decoration: underline;
  }
`

const Header = ({ copy, subcopy, boldedSubcopy, buttonText, buttonAction }) => (
  <HeaderContainer>
    <LogoContainer>
      <img alt="Acorns" src={AcornsLogoSvgPath} />
    </LogoContainer>
    <HeaderCopy>{copy}</HeaderCopy>
    {subcopy && (
      <HeaderSubcopy>
        {subcopy} <HeaderSubcopyBolded>{boldedSubcopy}</HeaderSubcopyBolded>
      </HeaderSubcopy>
    )}
    {buttonText && (
      <HeaderButton onClick={buttonAction}>{buttonText}</HeaderButton>
    )}
  </HeaderContainer>
)

const Layout: React.FC<Props> = ({
  copy,
  subcopy,
  boldedSubcopy,
  buttonText,
  buttonAction,
  ...props
}) => (
  <Container>
    <FormContainer>
      <TopLeaf src={TopLeavesSvgPath} />
      <BottomLeaf src={BottomLeavesSvgPath} />
      <Header
        copy={copy}
        subcopy={subcopy}
        boldedSubcopy={boldedSubcopy}
        buttonText={buttonText}
        buttonAction={buttonAction}
      />
      {props.children}
    </FormContainer>
  </Container>
)

export default Layout
